// Opportunity Ranking: filters, views, sort values and CSV export specification.
// Shared by the client table and the server export so the CSV is exactly the on-screen state.
import { REC, REC_ORDER, STATUS } from './constants.js';
import type { ModelDTO, ZipRow } from './types.js';

export type RankingView = 'remaining' | 'gains' | 'declines' | 'revenue' | 'share' | 'density' | 'all';
export interface SortState { key: string; dir: 'asc' | 'desc' }

export const VIEWS: [RankingView, string, SortState][] = [
  ['remaining', 'Largest remaining opportunity', { key: 'remaining', dir: 'desc' }],
  ['gains', 'Biggest gains', { key: 'chg', dir: 'desc' }],
  ['declines', 'Biggest declines', { key: 'chg', dir: 'asc' }],
  ['revenue', 'Highest revenue', { key: 'rev', dir: 'desc' }],
  ['share', 'Lowest share', { key: 'share', dir: 'asc' }],
  ['density', 'Opportunity density', { key: 'oppD', dir: 'desc' }],
  ['all', 'All ZIPs', { key: 'z', dir: 'asc' }]
];
export const STATUS_FILTERS: [string, string][] = [['service', 'Current service area'], ['established', 'Established'], ['new', 'New / Developing'], ['expansion', 'Expansion candidates'], ['removed', 'Inactive / Removed'], ['modeled', 'All modeled ZIPs']];

export interface RankingFilters { view: RankingView; county: string; status: string; rec: string; search: string; denom: string; dens: boolean }
export const DEFAULT_FILTERS: RankingFilters = { view: 'remaining', county: '', status: 'service', rec: '', search: '', denom: '', dens: false };

export function filterRanking(rows: readonly ZipRow[], f: RankingFilters): ZipRow[] {
  return rows.filter(r => {
    if (f.status === 'service' && !(r.status === 'established' || r.status === 'new')) return false;
    if (f.status === 'modeled' && !r.inUni) return false;
    if (!['service', 'modeled'].includes(f.status) && r.status !== f.status) return false;
    if (f.county && r.county !== f.county) return false;
    if (f.rec && r.rec !== f.rec) return false;
    if (f.denom === 'missing' && r.denom === 'ok') return false;
    if (f.view === 'gains' && !((r.revChange ?? 0) > 0)) return false;
    if (f.view === 'declines' && !((r.revChange ?? 0) < 0)) return false;
    if (f.search && !(r.z.includes(f.search) || (r.city || '').toLowerCase().includes(f.search.toLowerCase()))) return false;
    return true;
  });
}

/** Sort values per ranking column key (identical to the on-screen table). */
export function sortValue(key: string, flags: Record<string, string[]>): ((r: ZipRow) => string | number | null) | null {
  const m: Record<string, (r: ZipRow) => string | number | null> = {
    z: r => r.z, county: r => r.county || '', status: r => r.status, ooh: r => r.ooh, opp: r => r.popp, rev: r => r.rev,
    oohD: r => r.oohD, oppD: r => r.oppD, revD: r => r.revD, share: r => r.share, target: r => r.target, remaining: r => r.remaining,
    chg: r => r.revChange, pts: r => r.sharePts, rec: r => REC_ORDER.indexOf(r.rec as never), dq: r => (flags[r.z] || []).length
  };
  return m[key] || null;
}

/** Stable sort with missing values always last (same comparator as the approved DataTable). */
export function sortRows<T>(rows: readonly T[], sv: ((r: T) => string | number | null | undefined) | null, dir: 'asc' | 'desc'): T[] {
  if (!sv) return [...rows];
  const d = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = sv(a), y = sv(b);
    const xn = x == null || (typeof x === 'number' && isNaN(x)), yn = y == null || (typeof y === 'number' && isNaN(y));
    if (xn && yn) return 0; if (xn) return 1; if (yn) return -1;
    return typeof x === 'string' ? x.localeCompare(y as string) * d : ((x as number) - (y as number)) * d;
  });
}

/** Visible ranking columns, in display order (density columns follow Revenue when shown). */
export function rankingColumns(showDensity: boolean): string[] {
  const c = ['z', 'county', 'status', 'ooh', 'opp', 'rev', 'share', 'target', 'remaining', 'chg', 'pts', 'rec', 'dq'];
  if (showDensity) c.splice(c.indexOf('rev') + 1, 0, 'oohD', 'oppD', 'revD');
  return c;
}

export const COLUMN_LABELS: Record<string, string> = {
  z: 'ZIP', county: 'County', status: 'Status', ooh: 'OOH', opp: 'Opportunity', rev: 'Revenue', oohD: 'OOH density', oppD: 'Opp. density', revD: 'Revenue density',
  share: 'Share', target: 'Target', remaining: 'Remaining', chg: 'YoY Rev', pts: 'YoY Share', rec: 'Recommendation', dq: 'Data'
};

export function comparisonLabel(m: Pick<ModelDTO, 'period' | 'periods'>): string {
  const p = m.period;
  if (p.compare === 'provisional') return 'Provisional';
  if (p.compare === 'none') return 'No comparison';
  const pr = m.periods.find(x => x.id === p.prior);
  return 'vs ' + (pr ? `${pr.label} (${pr.range})` : 'prior period');
}

export interface ExportRequest { filters: RankingFilters; sort: SortState | null; columns: string[] }

/** Builds the Opportunity Ranking CSV: metadata header, visible columns, current sort and row order. */
export function rankingCsv(m: ModelDTO, req: ExportRequest, exportedAt: string): { csv: string; rows: number } {
  const flags = m.dq.zipFlags;
  const vdef = VIEWS.find(v => v[0] === req.filters.view) || VIEWS[0];
  const rows = filterRanking(m.rows, req.filters);
  const sort = req.sort || vdef[2];
  const sorted = sortRows(rows, sortValue(sort.key, flags), sort.dir);
  const cmpLbl = comparisonLabel(m);
  const P = m.period, pr = m.periods.find(x => x.id === P.prior), st = m.settings;
  const vint = (id: string) => m.vintages.find(v => v.id === id);
  const X: Record<string, [string, (r: ZipRow) => unknown][]> = {
    z: [['ZIP', r => r.z], ['City', r => r.city]], county: [['County', r => r.county || (r.countyTie ? 'Unassigned · HUD tie' : '')]], status: [['Service status', r => STATUS[r.status]]],
    ooh: [['OOH', r => (r.denom === 'ok' ? r.ooh : 'Invalid denominator')]],
    opp: [['Modeled opportunity', r => (r.popp != null ? r.popp : r.denom !== 'ok' ? 'Invalid denominator' : 'Not loaded')]], rev: [['Completed revenue', r => r.rev]],
    oohD: [['OOH density (OOH / sq mi)', r => (r.oohD != null ? r.oohD : 'Unavailable')]], oppD: [['Modeled opportunity density ($ / sq mi)', r => (r.oppD != null ? r.oppD : 'Unavailable')]],
    revD: [['Actual revenue density ($ / sq mi)', r => (r.revD != null ? r.revD : 'Unavailable')]],
    share: [['Estimated market share', r => (r.share != null ? r.share : r.denomPeriod === 'not-loaded' ? 'Not loaded' : r.denom !== 'ok' ? 'Invalid denominator' : 'Unavailable')]],
    target: [['Target share', r => r.target], ['Target revenue', r => (r.targetRev != null ? r.targetRev : 'Unavailable')]],
    remaining: [['Remaining opportunity', r => (r.remaining != null ? r.remaining : 'Unavailable')]],
    chg: [[`YoY revenue change (${cmpLbl})`, r => (r.revState === 'no-comparison' ? 'No comparison' : r.revChange)]],
    pts: [[`YoY share change, pts (${cmpLbl})`, r => (r.revState === 'no-comparison' ? 'No comparison' : r.sharePts)]],
    rec: [['Recommendation (V1 rules)', r => (r.rec ? REC[r.rec].label : '')]], dq: [['Data flags', r => (flags[r.z] || []).join('; ')]]
  };
  const spec = req.columns.flatMap(c => X[c] || []);
  const STF = STATUS_FILTERS.find(f => f[0] === req.filters.status);
  const meta: [string, string | number][] = [
    ['Selected period', `${P.label} · ${P.range}`], ['Comparison period', pr ? `${pr.label} · ${pr.range}` : 'None'],
    ['Comparison status', P.compare === 'ok' ? 'Valid comparable period' : P.compare === 'provisional' ? 'Provisional (overlapping windows; context only, not a recommendation input)' : 'No comparison'],
    ['Denominator mode', st.denomMode === 'constant' ? 'Constant denominator' : 'Historical denominator (period-appropriate)'],
    ['Denominator vintage', st.denomMode === 'constant' ? `${vint(st.constVintage)?.label ?? st.constVintage}${vint(st.constVintage)?.acsVintage ? ' · ' + vint(st.constVintage)!.acsVintage : ''}`
      : (P.vintage ? `${vint(P.vintage)?.label ?? P.vintage} · ACS vintage ${vint(P.vintage)?.acsVintage || 'Unknown / Not recorded'}` : 'Not loaded for this period')],
    ['Service area version', `${st.serviceAreaVersion} (${m.serviceSize} ZIPs)`], ['Methodology version', `${st.modelVersion} · $${st.oppPerOOH}/OOH · V1 recommendation rules`],
    ['Ranking view', vdef[1]],
    ['Filters', [req.filters.county && 'County: ' + req.filters.county, 'Status: ' + (STF ? STF[1] : req.filters.status), req.filters.rec && 'Recommendation: ' + (REC[req.filters.rec as keyof typeof REC]?.label ?? req.filters.rec), req.filters.search && 'Search: ' + req.filters.search, req.filters.denom && 'Missing denominator only'].filter(Boolean).join(' · ')],
    ['Sort', `${COLUMN_LABELS[sort.key] || sort.key} ${sort.dir === 'desc' ? 'descending' : 'ascending'}`],
    ['Density columns', req.columns.includes('oppD') ? 'Shown' : 'Hidden'], ['Rows', sorted.length], ['Exported', exportedAt]
  ];
  const q = (v: unknown) => (v == null ? '' : `"${String(v).replace(/"/g, '""')}"`);
  const csv = [...meta.map(([k, v]) => '# ' + k + ': ' + v), spec.map(s => q(s[0])).join(','), ...sorted.map(r => spec.map(s => q(s[1](r))).join(','))].join('\n');
  return { csv, rows: sorted.length };
}
